import { appConfig } from '../../constants/app-config';
import { languages } from '../../constants/languages';
import { markdownComponentModule } from '../../components/markdown/markdown';
import { identity, noop } from '../../utils/function-utils';
import { tryFunctionHelperModule } from '../../helpers/try-function';
import { eventEmitter } from '../../utils/event-utils';
import { toFiles as cccFieldsToFiles } from '../../../../../node_modules/@qualified/ccc-files/lib/cjs/index.js';

export const WORKSPACE_STATUS = {
  DISABLED: 'disabled',
  NEW: 'new',
  CONNECTING: 'connecting',
  CONNECTION_DELAY: 'connection-delay',
  CONNECTED: 'connected',
  CONNECTION_ERROR: 'connection-error',
  CONNECTION_RETRYING: 'connection-retrying',
  CONNECTION_FAILED: 'connection-failed',
  DESTROYING: 'destroying',
};

export const WORKSPACE_STATUS_LABELS = {
  [WORKSPACE_STATUS.DISABLED]: 'SmartIDE Disabled',
  [WORKSPACE_STATUS.NEW]: 'Starting SmartIDE',
  [WORKSPACE_STATUS.CONNECTING]: 'Connecting to SmartIDE',
  [WORKSPACE_STATUS.CONNECTION_DELAY]:
    'Connecting to SmartIDE is taking longer than expected',
  [WORKSPACE_STATUS.CONNECTED]:
    'SmartIDE connected and available for code completion and inspection',
  [WORKSPACE_STATUS.CONNECTION_ERROR]: 'Unable to connect to SmartIDE service',
  [WORKSPACE_STATUS.CONNECTION_RETRYING]:
    'SmartIDE service was interrupted, trying to reconnect',
  [WORKSPACE_STATUS.CONNECTION_FAILED]:
    'SmartIDE service cannot connect. Please restart the service.',
  [WORKSPACE_STATUS.DESTROYING]: 'Shutting down the SmartIDE service',
};

/**
 * Simple mapping of classic code challenge fields to files
 * @param {string} field - Field to map
 * @param {string} language - Language being used
 * @param {string?} testFramework - Framework being used (for `testCases` and `exampleTestCases` only)
 * @returns {string} Language Server filename for a given
 */
export function mapClassicCodeFieldToPath(field, language, testFramework) {
  if (/testcases/i.test(field)) {
    language = languages.frameworkLanguage(language, testFramework);
  }
  const lang = languages.getLang(language) || languages.Text;
  return `${field}.${lang.ext[0]}`;
}

const classFilePathMap = {
  code: 'solution',
  testCases: 'tests',
  setup: 'solution',
  preloaded: 'preloaded',
  exampleTestCases: 'tests',
  solution: 'solution',
};

/**
 * Sets the paths on classic code files. If we can map them using the ccc-files method, we use that. Otherwise,
 * we revert to a simple "field.ext" format.
 *
 * @param {Array<CodeFile>} files - Array of files to be updated
 * @param {string} language - current language
 * @param {string} testFramework - current test framework
 * @param {string?} languageVersion - optional language version
 * @param {string?} preset - optional runner preset
 * @param {Object?} $log - optional AngularJS $log service for debugging
 *
 * @returns {Array<{file:*, oldPath:string}>} Array of renamed files
 */
export function setClassicFilePaths({
  files,
  language,
  testFramework,
  languageVersion,
  preset,
  $log,
}) {
  // arguments passed into the ccc-files#toFiles method
  const toFilesArgs = {
    language,
    languageVersion,
    testFramework,
    preset,
  };
  // makes it easier to trace the files back to the field
  const fieldToFile = {};
  files.forEach((file) => {
    file._oldPath = file.path;
    // reset the path
    file.path = null;
    const mappedField = classFilePathMap[file.field];
    if (mappedField) {
      toFilesArgs[mappedField] = file.contents;
      fieldToFile[mappedField] = file;
    }
  });

  if (language && testFramework) {
    try {
      const mappedFiles = cccFieldsToFiles(toFilesArgs);
      mappedFiles.forEach((mf) => {
        // if we get mapped files back, store the path back on the file
        if (fieldToFile[mf.source]) {
          fieldToFile[mf.source].path = mf.path;
        }
      });
    } catch (e) {
      // Some errors are expected (e.g., unsupported language/framework combos), but log for debugging
      if ($log && $log.debug) {
        $log.debug(
          '[SmartIDE] File path mapping failed, using fallback paths:',
          {
            language,
            testFramework,
            languageVersion,
            preset,
            error: e.message || String(e),
          },
        );
      }
    }
  }

  const renamedFiles = [];

  files.forEach((file) => {
    if (!file.path) {
      // if a file didn't get a path mapped, we just revert to the simple classic file path
      file.path = mapClassicCodeFieldToPath(
        file.field,
        language,
        testFramework,
      );
    }

    if (file._oldPath !== file.path) {
      // track any files that got renamed
      renamedFiles.push({
        file,
        oldPath: file._oldPath,
      });
    }
    delete file._oldPath;
  });

  return renamedFiles;
}

const CONNECTING_WAIT_ERROR = 120000; // only wait 120s for a server to connect
const CONNECTING_WAIT_DELAY = 30000; // at 30s note there's a delay

const DEFAULT_PROXY_READY_TIMEOUT_MS = 120000;
const AUTOCOMPLETE_DURATION_CAP_MS = CONNECTING_WAIT_ERROR;
// Max time to wait for prewarm to complete before returning the proxy.
// Slow LSes (OmniSharp ~3-5s, JDT ~4s) need time to parse the project.
// If prewarm exceeds this, the proxy is returned anyway — the LS will
// still be loading but the user won't be blocked indefinitely.
const PREWARM_AWAIT_TIMEOUT_MS = 8000;

// Retry policy for the `language_server` create call. A single 500 from that
// endpoint used to end SmartIDE for the whole session (QUAL-2118), so we retry —
// but the assessment timer keeps running while we back off, which is exactly the
// harm that was reported, so the total added delay is tightly capped.
export const PROXY_CREATE_MAX_RETRIES = 3;
export const PROXY_CREATE_RETRY_BASE_MS = 1000;
export const PROXY_CREATE_RETRY_BUDGET_MS = 10000;

// Deadline for one request to the ephemeron API. `fetch` has no timeout of its
// own, and `DEFAULT_PROXY_READY_TIMEOUT_MS` is only checked between polls, so
// without this a single unanswered request runs past every budget we have —
// a candidate lost 85s to exactly one of them (QUAL-2250).
export const EPHEMERON_REQUEST_TIMEOUT_MS = 10000;
// Pace between polls of the pod-ready endpoint.
export const PROXY_POLL_INTERVAL_MS = 1000;
// Consecutive poll failures ridden out before giving up. The create call was
// answered seconds earlier, so a single blip says nothing about the pod; a run
// of them says the proxy is unreachable.
export const PROXY_POLL_MAX_NETWORK_FAILURES = 3;

// Sentry performance helpers for LSP observability
const sentryBreadcrumb = (message, data, level) => {
  if (window.Sentry) {
    window.Sentry.addBreadcrumb({
      category: 'smartide.lsp',
      message: message,
      data: data,
      level: level || 'info',
    });
  }
};

const sentryConfigureScope = (cb) => {
  if (window.Sentry && window.Sentry.configureScope) {
    try {
      window.Sentry.configureScope(cb);
    } catch (e) {
      // ignore
    }
  }
};

const sentrySetSmartIdeContext = (context) => {
  sentryConfigureScope(function (scope) {
    if (!scope || !context) return;

    // Tags: indexed + good for filtering/aggregation.
    if (context.smartIdeSessionId)
      scope.setTag('smartide.session_id', context.smartIdeSessionId);
    if (context.proxyId) scope.setTag('smartide.proxy_id', context.proxyId);
    if (context.proxyHost)
      scope.setTag('smartide.proxy_host', context.proxyHost);
    if (context.preset) scope.setTag('smartide.preset', context.preset);
    if (context.serverId) scope.setTag('smartide.server_id', context.serverId);
    if (context.challengeId) scope.setTag('challenge_id', context.challengeId);
    if (context.solutionId) scope.setTag('solution_id', context.solutionId);

    // Extras: helpful but not always indexed.
    scope.setExtra('smartide', Object.assign({}, context));
  });
};

const sentryClearSmartIdeContext = () => {
  sentryConfigureScope(function (scope) {
    if (!scope) return;
    scope.setTag('smartide.session_id', null);
    scope.setTag('smartide.proxy_id', null);
    scope.setTag('smartide.proxy_host', null);
    scope.setTag('smartide.preset', null);
    scope.setTag('smartide.server_id', null);
    scope.setTag('challenge_id', null);
    scope.setTag('solution_id', null);
    scope.setExtra('smartide', null);
  });
};

const generateSmartIdeSessionId = () =>
  [Date.now().toString(36), Math.random().toString(36).slice(2, 10)].join('-');

// --- SmartIDE: time to first autocomplete (typing -> first hint shown) ---
// We instrument CodeMirror at runtime to avoid depending on CodeMirrorWorkspace internals.
// 1) When the user first types in an editor, we record a timestamp on the CodeMirror instance.
// 2) When CodeMirror shows the first completion popup (showHint), we record the duration.
//
// NOTE: This is intentionally a breadcrumb-style metric (via sentryMeasure) because the app
// already ships Sentry and we want a low-friction, low-risk implementation.

const isLikelyUserTypingChange = (changeObj) => {
  // CodeMirror change origins we want to treat as "user started typing".
  // We intentionally exclude setValue and other programmatic updates.
  if (!changeObj || !changeObj.origin) return false;
  const origin = changeObj.origin;
  if (origin === 'setValue') return false;
  return origin === '+input' || origin === 'paste';
};

const TYPING_BLOCK_IDLE_MS = 1200;

const attachSmartIdeFirstTypingTracker = (
  codemirror,
  context,
  $interval,
  $timeout,
) => {
  if (!codemirror || codemirror.__smartIdeFirstTypingTrackerAttached) return;

  // Keep the context lean; this gets attached to the CodeMirror instance.
  codemirror.__smartIdeFirstTypingTrackerAttached = true;
  codemirror.__smartIdeAutocompleteContext = context || {};
  codemirror.__smartIdeFirstUserTypingAt = null;
  codemirror.__smartIdeFirstAutocompleteRecorded = false;
  codemirror.__smartIdeFirstAutocompleteTimeoutRecorded = false;
  codemirror.__smartIdeCurrentTypingBlockStartedAt = null;
  codemirror.__smartIdeLastTypingAt = null;
  codemirror.__smartIdeTypingVelocityWindowStartedAt = null;
  codemirror.__smartIdeTypingVelocityWindowCount = 0;
  codemirror.__smartIdeActivePollInterval = null;
  codemirror.__smartIdeActivePollTimeout = null;

  codemirror.on('change', function (_cm, changeObj) {
    if (!isLikelyUserTypingChange(changeObj)) return;

    var nowAt = Date.now();
    var ctx = codemirror.__smartIdeAutocompleteContext || {};
    var workspaceState = ctx.workspaceState;

    if (!codemirror.__smartIdeFirstUserTypingAt) {
      codemirror.__smartIdeFirstUserTypingAt = nowAt;

      if (
        workspaceState &&
        !workspaceState.firstTypingRecorded &&
        workspaceState.startTime
      ) {
        workspaceState.firstTypingRecorded = true;
        trackPerformance('smartide.time_to_first_typing', {
          duration_ms: nowAt - workspaceState.startTime,
          preset: ctx.preset,
          language: ctx.language,
          session_id: ctx.smartIdeSessionId,
          challenge_id: ctx.challengeId,
          solution_id: ctx.solutionId,
          assessment_id: ctx.assessmentId,
        });
      }

      sentryBreadcrumb('SmartIDE first user typing detected', {
        filePath: context && context.filePath,
        language: context && context.language,
        languageVersion: context && context.languageVersion,
        preset: context && context.preset,
        challengeId: context && context.challengeId,
        solutionId: context && context.solutionId,
      });
    }

    if (
      !codemirror.__smartIdeCurrentTypingBlockStartedAt ||
      !codemirror.__smartIdeLastTypingAt ||
      nowAt - codemirror.__smartIdeLastTypingAt > TYPING_BLOCK_IDLE_MS
    ) {
      codemirror.__smartIdeCurrentTypingBlockStartedAt = nowAt;
    }
    codemirror.__smartIdeLastTypingAt = nowAt;

    if (!codemirror.__smartIdeTypingVelocityWindowStartedAt) {
      codemirror.__smartIdeTypingVelocityWindowStartedAt = nowAt;
    }
    codemirror.__smartIdeTypingVelocityWindowCount += 1;

    var typingVelocityElapsedMs =
      nowAt - codemirror.__smartIdeTypingVelocityWindowStartedAt;
    if (typingVelocityElapsedMs >= 1000) {
      trackPerformance('smartide.key_typing_velocity', {
        value:
          (codemirror.__smartIdeTypingVelocityWindowCount * 1000) /
          typingVelocityElapsedMs,
        preset: ctx.preset,
        language: ctx.language,
        session_id: ctx.smartIdeSessionId,
        challenge_id: ctx.challengeId,
        solution_id: ctx.solutionId,
        assessment_id: ctx.assessmentId,
      });
      codemirror.__smartIdeTypingVelocityWindowStartedAt = nowAt;
      codemirror.__smartIdeTypingVelocityWindowCount = 0;
    }
  });

  codemirror.on('blur', function () {
    codemirror.__smartIdeCurrentTypingBlockStartedAt = null;
    codemirror.__smartIdeLastTypingAt = null;
  });

  // Per-instance keyup listener — polls cm.state.completionActive to detect when LSP
  // completions appear. We cannot patch window.CodeMirror.showHint because
  // CodeMirrorWorkspace uses its own bundled CodeMirror reference (not window.CodeMirror),
  // so the global showHint patch never intercepts LSP-driven completions.
  codemirror.on('keyup', function (cm) {
    if (!cm.__smartIdeCurrentTypingBlockStartedAt) return;

    // Cancel any existing poll+timeout from the previous keyup before starting new ones.
    // Without this, every keyup accumulates a new interval — if the user types N characters
    // before completions appear, N intervals all fire simultaneously and emit N duplicate
    // metrics, many with inflated durations anchored to the start of the typing block.
    if (cm.__smartIdeActivePollInterval) {
      $interval.cancel(cm.__smartIdeActivePollInterval);
      cm.__smartIdeActivePollInterval = null;
    }
    if (cm.__smartIdeActivePollTimeout) {
      $timeout.cancel(cm.__smartIdeActivePollTimeout);
      cm.__smartIdeActivePollTimeout = null;
    }

    var typingBlockStartedAt = cm.__smartIdeCurrentTypingBlockStartedAt;
    // Capture the time of this specific keyup so that autocomplete.general measures
    // "time from last keystroke to completion popup", not "time from block start".
    var keyupAt = Date.now();

    cm.__smartIdeActivePollInterval = $interval(function () {
      try {
        if (!(cm.state && cm.state.completionActive)) return;

        // Completions are now visible — stop polling and record metrics.
        $interval.cancel(cm.__smartIdeActivePollInterval);
        cm.__smartIdeActivePollInterval = null;

        var ctx = cm.__smartIdeAutocompleteContext || {};
        var autocompleteDurationMs = Date.now() - keyupAt;
        var cappedAutocompleteDurationMs = Math.min(
          autocompleteDurationMs,
          AUTOCOMPLETE_DURATION_CAP_MS,
        );

        var tabVisible = document.visibilityState !== 'hidden';

        if (!cm.__smartIdeFirstAutocompleteRecorded) {
          // Record first autocomplete separately — don't pollute the
          // general metric with the slow first-completion time.
          cm.__smartIdeFirstAutocompleteRecorded = true;

          if (typingBlockStartedAt) {
            var firstAutocompleteDurationMs = Math.min(
              Date.now() - typingBlockStartedAt,
              AUTOCOMPLETE_DURATION_CAP_MS,
            );
            trackPerformance('smartide.time_to_first_autocomplete', {
              duration_ms: firstAutocompleteDurationMs,
              preset: ctx.preset,
              language: ctx.language,
              tab_visible: tabVisible,
              session_id: ctx.smartIdeSessionId,
              status: 'ok',
              reason: 'completion_detected',
              challenge_id: ctx.challengeId,
              solution_id: ctx.solutionId,
              assessment_id: ctx.assessmentId,
            });
          }
        } else {
          // Only track general autocomplete after the first one, so the
          // metric reflects steady-state LS performance, not cold start.
          trackPerformance('smartide.autocomplete.general', {
            duration_ms: cappedAutocompleteDurationMs,
            preset: ctx.preset,
            language: ctx.language,
            tab_visible: tabVisible,
            session_id: ctx.smartIdeSessionId,
            challenge_id: ctx.challengeId,
            solution_id: ctx.solutionId,
            assessment_id: ctx.assessmentId,
          });
        }

        cm.__smartIdeCurrentTypingBlockStartedAt = null;
      } catch (e) {
        // Never let observability code impact editor functionality.
        $interval.cancel(cm.__smartIdeActivePollInterval);
        cm.__smartIdeActivePollInterval = null;
      }
    }, 20);

    // Safety valve — stop polling after 15 seconds (completions should appear quickly).
    cm.__smartIdeActivePollTimeout = $timeout(function () {
      if (cm.__smartIdeActivePollInterval) {
        $interval.cancel(cm.__smartIdeActivePollInterval);
        cm.__smartIdeActivePollInterval = null;
      }
      cm.__smartIdeActivePollTimeout = null;

      if (!cm.__smartIdeCurrentTypingBlockStartedAt) return;
      if (cm.__smartIdeCurrentTypingBlockStartedAt !== typingBlockStartedAt) {
        return;
      }
      if (cm.state && cm.state.completionActive) return;

      var timeoutCtx = cm.__smartIdeAutocompleteContext || {};
      trackPerformance('smartide.autocomplete.timeout', {
        preset: timeoutCtx.preset,
        language: timeoutCtx.language,
        session_id: timeoutCtx.smartIdeSessionId,
        status: 'timeout',
        reason: 'no_completion_active',
        challenge_id: timeoutCtx.challengeId,
        solution_id: timeoutCtx.solutionId,
        assessment_id: timeoutCtx.assessmentId,
      });

      if (
        cm.__smartIdeFirstAutocompleteRecorded ||
        cm.__smartIdeFirstAutocompleteTimeoutRecorded
      ) {
        return;
      }

      cm.__smartIdeFirstAutocompleteTimeoutRecorded = true;
      var timeoutDurationMs = Math.min(
        Date.now() - typingBlockStartedAt,
        AUTOCOMPLETE_DURATION_CAP_MS,
      );

      trackPerformance('smartide.first_autocomplete_timeout', {
        duration_ms: timeoutDurationMs,
        preset: timeoutCtx.preset,
        language: timeoutCtx.language,
        session_id: timeoutCtx.smartIdeSessionId,
        status: 'timeout',
        reason: 'no_completion_active',
        challenge_id: timeoutCtx.challengeId,
        solution_id: timeoutCtx.solutionId,
        assessment_id: timeoutCtx.assessmentId,
      });
    }, 15000);
  });
};

const installSmartIdeAutocompleteInstrumentation = () => {
  // No-op: per-instance keyup polling in attachSmartIdeFirstTypingTracker handles
  // autocomplete detection. The window.CodeMirror.showHint patch approach was
  // ineffective because CodeMirrorWorkspace uses its own bundled CodeMirror reference.
};

// Sends a performance metric to the Rails backend, which fans it out to:
//   - StatsD  → Datadog dashboard widgets (avg, p95, count, breakdown by preset/language)
//   - Structured log → Datadog Log Management with full context (userId, challengeId, etc.)
//
// Fire-and-forget: errors are swallowed so broken telemetry never affects the editor.
// High-cardinality IDs (challengeId, solutionId, assessmentId) are included in the
// POST body but the Rails controller routes them to logs only — never to StatsD tags.
function trackPerformance(event, data) {
  try {
    if (!window.fetch) return;
    window
      .fetch(appConfig.apiRoot + 'lsp_telemetry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify(Object.assign({ event: event }, data || {})),
      })
      .catch(function () {});
  } catch (e) {
    // Never let telemetry break the editor.
  }
}

function sentryMeasure(name, durationMs, data) {
  if (window.Sentry) {
    window.Sentry.addBreadcrumb({
      category: 'smartide.performance',
      message: name,
      data: Object.assign({}, data || {}, { durationMs: durationMs }),
      level: durationMs > 10000 ? 'warning' : 'info',
    });
    // Also capture as a Sentry event if it's abnormally slow
    if (durationMs > 30000) {
      window.Sentry.captureMessage(
        '[SmartIDE] Slow LSP operation: ' + name + ' (' + durationMs + 'ms)',
        {
          level: 'warning',
          extra: Object.assign({}, data || {}, { durationMs: durationMs }),
        },
      );
    }
  }
}

// we can get away with async/await here because this service doesn't interact with AngularJS
// Normally we'd have to convert this to an AngularJS component, and also convert it to use Promises.
// This class is not currently exported on purpose (we don't want it used outside this factory)
// DO NOT USE ASYNC/AWAIT WITH ANGULARJS

const now = () => Math.floor(Date.now() / 1000);
// eslint-disable-next-line angular/timeout-service
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether a failed `create()` call is worth trying again.
 *
 * Only server-side faults and network blips can succeed on a retry. A 4xx will
 * fail identically, and a `SmartIDEProxyWaitTimeout` has already burned
 * `DEFAULT_PROXY_READY_TIMEOUT_MS` of the candidate's time.
 *
 * @param {Error?} err - Error thrown by `create()`
 * @returns {boolean} True if the call should be retried
 */
export function isRetryableProxyCreateError(err) {
  if (!err) return false;
  if (isNetworkLevelError(err)) return true;
  if (err.name !== 'SmartIDEProxyCreateError') return false;
  const status = err.details && err.details.statusCode;
  return typeof status === 'number' && status >= 500;
}

/**
 * Whether a request failed without ever coming back with an answer.
 *
 * `TypeError` is how `fetch` reports a request that never reached the server;
 * `AbortError` is our own `EPHEMERON_REQUEST_TIMEOUT_MS` firing. Neither tells
 * us anything about the state of what we asked about, so both are worth asking
 * again. Anything else is a real answer, to act on rather than retry.
 *
 * @param {Error?} err - Error thrown by the request
 * @returns {boolean} True if the request is worth retrying
 */
export function isNetworkLevelError(err) {
  if (!err) return false;
  return err.name === 'TypeError' || err.name === 'AbortError';
}

/**
 * `fetch` with a deadline.
 *
 * A bare `fetch` has no timeout: a request that opens a connection and then
 * goes unanswered hangs for as long as the OS keeps retransmitting.
 *
 * @param {string} url - Request URL
 * @param {Object} options - `fetch` options
 * @param {number?} timeoutMs - Abort the request after this long
 * @param {Function?} fetchFn - Injectable for tests
 * @returns {Promise} Resolves with the Response, rejects with `AbortError` on timeout
 */
export async function fetchWithTimeout(
  url,
  options,
  timeoutMs = EPHEMERON_REQUEST_TIMEOUT_MS,
  fetchFn = fetch,
) {
  const controller = new AbortController();
  // eslint-disable-next-line angular/timeout-service
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchFn(
      url,
      Object.assign({}, options, { signal: controller.signal }),
    );
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Exponential backoff with +/-25% jitter, so a degraded proxy pool isn't
 * stampeded by every candidate retrying in lockstep.
 *
 * @param {number} attempt - Zero-based attempt index
 * @param {Function?} random - Source of randomness, injectable for tests
 * @returns {number} Delay in milliseconds
 */
export function proxyCreateRetryDelay(attempt, random = Math.random) {
  const base = PROXY_CREATE_RETRY_BASE_MS * Math.pow(2, attempt);
  return Math.round(base * (0.75 + random() * 0.5));
}

/**
 * Runs `create` until it succeeds, the error is not retryable, or the retry
 * budget is spent. Rethrows the last error when it gives up.
 *
 * @param {Function} create - Returns a Promise for the created proxy
 * @param {Object?} options - Overrides, all injectable for tests
 * @param {number?} options.maxRetries - Retries after the initial attempt
 * @param {number?} options.budgetMs - Ceiling on total time spent sleeping
 * @param {Function?} options.sleep - Delay function
 * @param {Function?} options.random - Source of randomness for jitter
 * @param {Function?} options.onRetry - Called as (err, attemptNumber, waitMs)
 * @returns {Promise} Resolves with whatever `create` resolved with
 */
export async function retryProxyCreate(create, options = {}) {
  const {
    maxRetries = PROXY_CREATE_MAX_RETRIES,
    budgetMs = PROXY_CREATE_RETRY_BUDGET_MS,
    sleep = delay,
    random = Math.random,
    onRetry = noop,
  } = options;

  let spentMs = 0;
  for (let attempt = 0; ; attempt++) {
    try {
      return await create(attempt);
    } catch (err) {
      const waitMs = proxyCreateRetryDelay(attempt, random);
      if (
        attempt >= maxRetries ||
        spentMs + waitMs > budgetMs ||
        !isRetryableProxyCreateError(err)
      ) {
        throw err;
      }
      onRetry(err, attempt + 1, waitMs);
      spentMs += waitMs;
      await sleep(waitMs);
    }
  }
}

/**
 * Rebinds the editors a candidate has open onto a freshly respawned proxy.
 *
 * The replacement pod is a new pod with an image-default workspace: its files
 * only ever come from `writeFiles` (prewarm just opens one document over a
 * throwaway socket). So the challenge files have to be written again before the
 * editors reattach, or cross-file resolution -- imports, sibling modules,
 * `$ref/` files -- is silently dead on the new proxy.
 *
 * @param {Object} options - Injected so this stays testable outside AngularJS
 * @param {Object} options.svc - The workspace service
 * @param {Array} options.editors - `{file, codemirror}` pairs to reattach
 * @param {Array?} options.files - Current challenge files to seed the pod with
 * @param {Function} options.all - `Promise.all` equivalent (`$q.all` in the app)
 * @returns {Promise} Resolves once the editors are reattached
 */
export function reattachEditors({ svc, editors, files, all }) {
  // Close first: the documents still hold a handle to the dead connection.
  return all(editors.map((e) => svc.closeFile(e.file)))
    .then(() =>
      Array.isArray(files) && files.length ? svc.initializeFiles(files) : null,
    )
    .then(() => all(editors.map((e) => svc.openEditor(e.file, e.codemirror))));
}

/**
 * Builds a `report(label, err, data)` that sends each distinct failure to
 * Sentry once instead of once per occurrence.
 *
 * A dead LSP proxy fails every subsequent file operation, and the workspace
 * catch handlers hang off `updateFile`/`saveOpenFile`, which fire per edit and
 * per autosave. Reporting unconditionally meant one Sentry event per keystroke
 * for the rest of the session: `[SmartIDE] Workspace operation failed:` billed
 * 1.67M events in the twelve days after it shipped, with single sessions
 * responsible for 28k, and it was 89% of the org's entire error quota.
 *
 * The tally is per-reporter and in-memory, so a workspace that is torn down and
 * rebuilt re-reports its first failure -- that is deliberate, since a failure
 * that survives a rebuild is worth seeing again. If the repeat volume itself
 * becomes the interesting signal, have the dispose path log the counts.
 *
 * @param {Object} log - Angular's `$log` (`$log.error` forwards to Sentry)
 * @returns {Function} `report(label, err, data)`
 */
export function createFailureReporter(log) {
  const seen = new Map();

  return function report(label, err, data) {
    // Keyed by label too, so the cleanup path is not silenced by an identical
    // message already reported from the workspace path.
    const key = label + ':' + (err.message || String(err));
    const count = (seen.get(key) || 0) + 1;
    seen.set(key, count);
    if (count > 1) return false;

    log.error(label, data, err);
    return true;
  };
}

// Pods pre-created from the assessment lobby (keyed by preset name).
// Consumed on first use; one pod per preset, fire-and-forget errors fall back to normal flow.
const _preWarmedProxies = new Map();
const urlPath = (url, path) => url + (url.endsWith('/') ? '' : '/') + path;

const parseUidFromTokenSub = (sub) => {
  if (!sub || typeof sub !== 'string') return '';
  const idx = sub.indexOf('.');
  if (idx <= 0) return '';
  return sub.slice(0, idx);
};

class LanguageServerController {
  constructor({
    url,
    authorize,
    runConfig,
    $log,
    context,
    initialFilesGetter,
  }) {
    this.token = '';
    this.exp = 0;
    this.url = url;
    this.authorize = authorize;
    this.$log = $log;
    this.context = context || {};
    this.startupAttemptSent = false;
    this.startupResultSent = false;
    this.respawned = false;
    this.initialFilesGetter = initialFilesGetter || null;
    this.name = [
      runConfig.language,
      runConfig.languageVersion,
      runConfig.preset || 'default',
    ].join('/');
    // Create a promise that resolves with Proxy service's info.
    this.proxy = this.createProxy();
  }

  /**
   * Ensure proxy is created, and resolve with WebSocket URL for the Language Server.
   */
  async start(name) {
    if (!this.proxy) this.proxy = this.createProxy();

    const proxy = await this.proxy;
    return `${proxy.proxyUrl}?name=${name}`;
  }

  async createProxy() {
    var proxyStartTime = Date.now();
    this.emitStartupAttempt();
    sentryBreadcrumb('Creating LSP proxy', { preset: this.name });

    const _cached = _preWarmedProxies.get(this.name);
    if (_cached) {
      _preWarmedProxies.delete(this.name);
      try {
        const proxy = await _cached;
        if (proxy) {
          sentryMeasure('lsp.proxy.total', Date.now() - proxyStartTime, {
            preset: this.name,
            proxyId: proxy.id,
          });
          sentrySetSmartIdeContext(
            Object.assign({}, this.context || {}, {
              proxyId: proxy.id,
              preset: this.name,
            }),
          );
          sentryBreadcrumb('Using pre-warmed LSP proxy', {
            preset: this.name,
            proxyId: proxy.id,
          });
          this.emitStartupResult('connected', { reason: 'pre_warmed' });
          // Resolve this.proxy now so prewarm() can access proxy.id without circular deadlock,
          // then fire prewarm v3 with the real challenge file content.
          this.proxy = Promise.resolve(proxy);
          Promise.race([this.prewarm(), delay(PREWARM_AWAIT_TIMEOUT_MS)]).catch(
            () => {},
          );
          return proxy;
        }
      } catch (e) {
        sentryBreadcrumb(
          'Pre-warmed proxy failed, creating new',
          { preset: this.name },
          'warning',
        );
      }
    }

    var id = null;
    var host = null;
    var tls = false;

    try {
      var createStartTime = Date.now();
      const json = await retryProxyCreate(() => this.create(this.name), {
        onRetry: (retryErr, attempt, waitMs) => {
          sentryBreadcrumb(
            'Retrying LSP proxy creation',
            {
              preset: this.name,
              attempt: attempt,
              waitMs: waitMs,
              statusCode: retryErr.details && retryErr.details.statusCode,
              errorName: retryErr.name,
            },
            'warning',
          );
        },
      });
      sentryMeasure('lsp.ephemeron.create', Date.now() - createStartTime, {
        preset: this.name,
        proxyId: json.id,
      });

      id = json.id;
      var waitStartTime = Date.now();
      const waitResult = await this.wait(id);
      host = waitResult.host;
      tls = waitResult.tls;
      sentryMeasure('lsp.ephemeron.wait', Date.now() - waitStartTime, {
        preset: this.name,
        proxyId: id,
      });

      var totalDuration = Date.now() - proxyStartTime;
      sentryMeasure('lsp.proxy.total', totalDuration, {
        preset: this.name,
        proxyId: id,
        host: host,
      });
      sentryBreadcrumb('LSP proxy created', {
        preset: this.name,
        proxyId: id,
        host: host,
        durationMs: totalDuration,
      });

      // Make proxy metadata available on all subsequent Sentry events for this workspace.
      sentrySetSmartIdeContext(
        Object.assign({}, this.context || {}, {
          proxyId: id,
          proxyHost: host,
          preset: this.name,
        }),
      );

      const proxy = {
        id,
        proxyUrl: `ws${tls ? 's' : ''}://${host}`,
        filesUrl: `http${tls ? 's' : ''}://${host}/files`,
        // ping before it expires to extend its lifetime
        // eslint-disable-next-line angular/interval-service
        timer: setInterval(this.createPinger(id), 60 * 4 * 1000),
      };

      // Prewarm the language server before the client connects. We await it
      // (with a timeout) so the LS has finished parsing the real file by the
      // time the user starts typing. Without this, slow LSes like OmniSharp
      // would still be loading when the first completion fires.
      var prewarmStartTime = Date.now();
      try {
        await Promise.race([this.prewarm(), delay(PREWARM_AWAIT_TIMEOUT_MS)]);
      } catch (e) {
        // Prewarm failure is non-critical — the LS will still work,
        // just with a slower first autocomplete.
        sentryBreadcrumb(
          'SmartIDE early prewarm failed',
          {
            preset: this.name,
            errorName: e && e.name,
            errorMessage: e && e.message,
          },
          'warning',
        );
      }
      sentryMeasure('lsp.prewarm.await', Date.now() - prewarmStartTime, {
        preset: this.name,
        proxyId: id,
      });

      return proxy;
    } catch (err) {
      sentryBreadcrumb(
        'LSP proxy creation failed',
        {
          preset: this.name,
          proxyId: id,
          errorName: err && err.name,
          errorMessage: err && err.message,
          durationMs: Date.now() - proxyStartTime,
        },
        'error',
      );
      if (err && err.name === 'SmartIDEPodStartupError') {
        const podReason = err.details && err.details.reason;
        this.emitStartupResult('pod_startup_error', { reason: podReason });
        trackPerformance('smartide.lsp.startup_error', {
          preset: this.name,
          language: this.context && this.context.language,
          session_id: this.context && this.context.smartIdeSessionId,
          status: 'error',
          reason: podReason,
          challenge_id: this.context && this.context.challengeId,
          solution_id: this.context && this.context.solutionId,
          assessment_id: this.context && this.context.assessmentId,
        });
      } else if (err && err.name === 'SmartIDEProxyWaitTimeout') {
        this.emitStartupResult('timeout', {
          reason: 'lsp_pod_ready_wait_timeout',
        });
        trackPerformance('smartide.proxy_wait_timeout', {
          preset: this.name,
          language: this.context && this.context.language,
          session_id: this.context && this.context.smartIdeSessionId,
          status: 'timeout',
          reason: 'lsp_pod_ready_wait_timeout',
          challenge_id: this.context && this.context.challengeId,
          solution_id: this.context && this.context.solutionId,
          assessment_id: this.context && this.context.assessmentId,
        });
      } else if (err && err.name === 'SmartIDEProxyCreateError') {
        const createReason = err.details && err.details.reason;
        this.emitStartupResult('connection_failed', {
          reason: 'proxy_create_error',
        });
        trackPerformance('smartide.lsp.startup_error', {
          preset: this.name,
          language: this.context && this.context.language,
          session_id: this.context && this.context.smartIdeSessionId,
          status: 'error',
          reason: createReason,
          challenge_id: this.context && this.context.challengeId,
          solution_id: this.context && this.context.solutionId,
          assessment_id: this.context && this.context.assessmentId,
        });
      } else {
        this.emitStartupResult('connection_failed', {
          reason: 'proxy_creation_failed',
        });
      }
      throw err;
    } finally {
      // startup telemetry is recorded when workspace.openTextDocument resolves,
      // which is our closest "socket ready" signal from CodeMirrorWorkspace.
    }
  }

  /**
   * Replaces a proxy that died mid-session with a fresh one.
   *
   * One-shot on purpose: if the replacement also dies, something is wrong on the
   * server side and looping would just spend the candidate's remaining time.
   *
   * @returns {Promise<Object|null>} The new proxy, or null if already respawned
   */
  async respawn() {
    if (this.respawned) return null;
    this.respawned = true;

    try {
      await this.destroyProxy();
    } catch (e) {
      // The old proxy is already gone — that is why we're here.
      sentryBreadcrumb(
        'Discarding unreachable LSP proxy',
        { preset: this.name, errorName: e && e.name },
        'warning',
      );
    }
    this.proxy = null;

    sentryBreadcrumb('Respawning LSP proxy', { preset: this.name }, 'warning');
    // Let the respawn report its own outcome rather than inheriting the
    // already-sent result of the original startup.
    this.startupResultSent = false;
    this.proxy = this.createProxy();
    return this.proxy;
  }

  async destroyProxy() {
    if (!this.proxy) return;

    const proxy = await this.proxy;
    clearInterval(proxy.timer);
    const id = proxy.id;
    await this.auth();
    await fetch(urlPath(this.url, id), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
    });
    this.proxy = null;
  }

  async wait(id, timeoutMs) {
    // Poll until ready
    let json;
    var pollCount = 0;
    var networkFailures = 0;
    var startTime = Date.now();
    var timeout =
      typeof timeoutMs === 'number'
        ? timeoutMs
        : DEFAULT_PROXY_READY_TIMEOUT_MS;
    while (!(json && json.host)) {
      if (Date.now() - startTime > timeout) {
        const err = new Error(
          '[SmartIDE] Timed out waiting for LSP pod to become ready',
        );
        err.name = 'SmartIDEProxyWaitTimeout';
        err.details = {
          proxyId: id,
          preset: this.name,
          timeoutMs: timeout,
          pollCount: pollCount,
        };
        if (this.$log && this.$log.error) {
          this.$log.error(
            '[SmartIDE] LSP pod ready wait timed out',
            Object.assign({}, this.context || {}, err.details),
            err,
          );
        }
        throw err;
      }
      await this.auth();
      var pollStartTime = Date.now();
      let response;
      try {
        response = await fetchWithTimeout(urlPath(this.url, id), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
        });
      } catch (err) {
        // Losing one poll is not losing the pod — it is still coming up on the
        // other side. Throwing here ended SmartIDE for the rest of the
        // assessment, because nothing retries a failed proxy creation.
        if (!isNetworkLevelError(err)) throw err;
        networkFailures++;
        if (networkFailures > PROXY_POLL_MAX_NETWORK_FAILURES) throw err;

        sentryBreadcrumb(
          'LSP pod poll failed, retrying',
          {
            proxyId: id,
            preset: this.name,
            consecutiveFailures: networkFailures,
            errorName: err && err.name,
          },
          'warning',
        );
        // Pace from the start of the failed poll, not from now: a request that
        // burned the full deadline has already left the pod alone long enough.
        await delay(
          Math.max(0, PROXY_POLL_INTERVAL_MS - (Date.now() - pollStartTime)),
        );
        continue;
      }
      // A poll that answered clears the streak — the budget is consecutive.
      networkFailures = 0;
      if (!response.ok) {
        const errorJson = await response.json().catch(() => ({}));
        const err = new Error('[SmartIDE] LSP pod failed to start');
        err.name = 'SmartIDEPodStartupError';
        err.details = {
          proxyId: id,
          preset: this.name,
          statusCode: response.status,
          reason: errorJson.reason || 'unknown',
          message: errorJson.message || '',
        };
        throw err;
      }
      json = await response.json();
      pollCount++;
      if (json.host) {
        sentryBreadcrumb('LSP pod ready', {
          proxyId: id,
          pollCount: pollCount,
          pollDurationMs: Date.now() - pollStartTime,
        });
        break;
      }
      if (pollCount % 5 === 0) {
        sentryBreadcrumb(
          'LSP pod still waiting',
          { proxyId: id, pollCount: pollCount },
          'warning',
        );
      }
      await delay(PROXY_POLL_INTERVAL_MS);
    }
    return json;
  }

  async create(preset) {
    await this.auth();
    const custom = {};
    const context = this.context || {};
    if (context.candidateId) custom.candidateId = String(context.candidateId);
    if (context.teamMemberId)
      custom.teamMemberId = String(context.teamMemberId);
    if (context.assessmentId)
      custom.assessmentId = String(context.assessmentId);
    if (context.challengeId) custom.challengeId = String(context.challengeId);
    if (context.solutionId) custom.solutionId = String(context.solutionId);

    const body = {
      preset,
      lifetimeMinutes: 5,
    };
    if (Object.keys(custom).length) {
      body.custom = custom;
    }

    // Deadlined for the same reason the poll is: an unanswered POST here hangs
    // the whole creation before the poll's protection can apply, and
    // `retryProxyCreate`'s budget only caps the sleeping between attempts.
    const response = await fetchWithTimeout(this.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      // Without this the body of a 500 (`{reason: "internal server error"}`) was
      // returned as if it were a proxy, and the failure only surfaced later as an
      // unrelated wait error against an undefined proxy id.
      const errorJson = await response.json().catch(() => ({}));
      const err = new Error('[SmartIDE] Failed to create LSP proxy');
      err.name = 'SmartIDEProxyCreateError';
      err.details = {
        preset,
        statusCode: response.status,
        reason: errorJson.reason || 'unknown',
        message: errorJson.message || '',
      };
      throw err;
    }
    return await response.json();
  }

  async auth() {
    if (now() + 30 >= this.exp) {
      var authStartTime = Date.now();
      this.token = await this.authorize();
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      this.exp = payload.exp;
      const uid = parseUidFromTokenSub(payload.sub);
      if (uid) {
        if (appConfig.candidateApp) {
          if (!this.context.candidateId) this.context.candidateId = uid;
        } else {
          if (!this.context.teamMemberId) this.context.teamMemberId = uid;
        }
      }
      sentryMeasure('lsp.auth', Date.now() - authStartTime, {
        preset: this.name,
      });
    }
  }

  createPinger(id) {
    const url = urlPath(this.url, id);
    return async () => {
      await this.auth();
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        // Update the expiration time to 5 minutes from now.
        body: JSON.stringify({ lifetimeMinutes: 5 }),
      });
    };
  }

  async writeFiles(files) {
    const operations = files.map((file) => ({
      op: 'write',
      path: file.path,
      contents: file.contents,
    }));
    return this.changeRemoteFiles(operations);
  }

  async writeFile(file) {
    return this.changeRemoteFiles([
      { op: 'write', path: file.path, contents: file.contents },
    ]);
  }

  async removeFile(path) {
    return this.changeRemoteFiles([{ op: 'remove', path }]);
  }

  async renameFile(from, to) {
    return this.changeRemoteFiles([{ op: 'rename', from, to }]);
  }

  async changeRemoteFiles(operations) {
    if (!this.proxy) return;

    const proxy = await this.proxy;
    const res = await fetch(proxy.filesUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ operations }),
    });
    const json = await res.json();
    if (!res.ok) {
      // Handle file operation errors
      const errorDetails = {
        status: res.status,
        statusText: res.statusText,
        operations: operations.map((op) => ({
          op: op.op,
          path: op.path || op.from,
        })),
        errors: json.errors || [],
        languageServer: this.name,
      };

      const error = new Error(
        `[SmartIDE] File operation failed: ${res.statusText}`,
      );
      error.name = 'SmartIDEFileOperationError';
      error.details = errorDetails;

      // Log error details - will be sent to Sentry via $log.error
      if (this.$log && this.$log.error) {
        this.$log.error(
          '[SmartIDE] File operation error:',
          errorDetails,
          error,
        );
      }

      throw error;
    }
    // `changes` is an array of `FileEvent`.
    return json.changes;
  }

  async prewarm() {
    if (!this.proxy) return;

    const proxy = await this.proxy;
    if (!proxy || !proxy.id) return;

    await this.auth();

    // Resolve the primary solution file for prewarm v3.
    // Pick a source file the LS can actually analyze — skip markdown, text,
    // config, and other files the language server won't benefit from parsing.
    var primaryFile = null;
    if (this.initialFilesGetter) {
      try {
        var initFiles = this.initialFilesGetter();
        if (Array.isArray(initFiles)) {
          var lang = this.name.split('/')[0];
          var sourceExts = {
            javascript: ['.js', '.jsx', '.mjs'],
            typescript: ['.ts', '.tsx'],
            python: ['.py'],
            csharp: ['.cs'],
            ruby: ['.rb'],
            java: ['.java'],
          };
          var exts = sourceExts[lang] || [];

          var isSourceFile = function (f) {
            if (!f || !f.path || !f.contents) return false;
            if (f.directory || f.hidden || f.reference) return false;
            if (!exts.length) return true;
            var p = f.path.toLowerCase();
            return exts.some(function (ext) {
              return p.endsWith(ext);
            });
          };

          // Prefer solution file over fixture/test file.
          primaryFile =
            initFiles.find(function (f) {
              return isSourceFile(f) && !/test|spec|fixture/i.test(f.path);
            }) ||
            initFiles.find(function (f) {
              return isSourceFile(f);
            });
        }
      } catch (e) {
        // non-critical — fall back to v2 prewarm
      }
    }

    var prewarmVersion = primaryFile ? 'v3' : 'v2';

    sentryBreadcrumb('SmartIDE prewarm request start', {
      proxyId: proxy.id,
      preset: this.name,
      prewarm_version: prewarmVersion,
    });

    var reqBody = { name: this.name };
    if (primaryFile) {
      reqBody.fileUri = 'source://' + primaryFile.path;
      // Cap at 50 KB to avoid blowing up the request.
      reqBody.fileContent = (primaryFile.contents || '').substring(0, 50000);
      // Derive languageId from the preset name (e.g. "typescript/5.7/default" → "typescript").
      reqBody.fileLanguageId = this.name.split('/')[0];
    }

    const res = await fetch(urlPath(this.url, `${proxy.id}/prewarm`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify(reqBody),
    });

    let body = null;
    try {
      body = await res.json();
    } catch (e) {
      // best-effort parse for observability only
    }

    sentryBreadcrumb(
      'SmartIDE prewarm request complete',
      {
        proxyId: proxy.id,
        statusCode: res.status,
        warmed: body && body.warmed,
        reason: body && body.reason,
        prewarm_version: prewarmVersion,
      },
      res.ok ? 'info' : 'warning',
    );
  }

  emitStartupAttempt() {
    if (this.startupAttemptSent) return;
    this.startupAttemptSent = true;
    trackPerformance('smartide.lsp.startup_attempt', {
      preset: this.name,
      language: this.context && this.context.language,
      session_id: this.context && this.context.smartIdeSessionId,
      challenge_id: this.context && this.context.challengeId,
      solution_id: this.context && this.context.solutionId,
      assessment_id: this.context && this.context.assessmentId,
    });
  }

  emitStartupResult(status, extra) {
    if (this.startupResultSent) return;
    this.startupResultSent = true;
    trackPerformance(
      'smartide.lsp.startup_result',
      Object.assign(
        {
          preset: this.name,
          language: this.context && this.context.language,
          session_id: this.context && this.context.smartIdeSessionId,
          status: status,
          challenge_id: this.context && this.context.challengeId,
          solution_id: this.context && this.context.solutionId,
          assessment_id: this.context && this.context.assessmentId,
        },
        extra || {},
      ),
    );
  }
}

export const codemirrorWorkspaceServiceModule = angular
  .module('Q.services.codemirror-workspace-service', [
    markdownComponentModule,
    tryFunctionHelperModule,
  ])

  /**
   * A service to help simplify the process of enabling codemirror editors with LSP
   */
  .factory(
    'codemirrorWorkspaceService',
    function (
      $window,
      $http,
      $log,
      configService,
      $q,
      marked,
      tryFunction,
      $interval,
      $timeout,
    ) {
      return {
        /**
         * Pre-creates an LSP pod for a preset so it's warmed by the time the candidate opens the challenge.
         * Called from the assessment lobby when the candidate clicks Start/Resume.
         * Fire-and-forget — errors fall back to normal pod creation.
         *
         * @param {string} preset - e.g. 'javascript/18.x/default'
         * @param {string} teamId - Used to obtain a service_auth token
         */
        preWarmProxy({ preset, teamId, initialFiles }) {
          if (_preWarmedProxies.has(preset)) return;
          const parts = preset.split('/');
          const authorize = () =>
            $http
              .post(`${appConfig.baseApiRoot}service_auth`, {
                service: 'language_server',
                teamId,
              })
              .then((resp) => resp.data.token);
          const controller = new LanguageServerController({
            url: configService.config.languageServerUrl,
            authorize,
            runConfig: {
              language: parts[0],
              languageVersion: parts[1],
              preset: parts[2] || 'default',
              languageServers: [],
            },
            $log,
            context: {},
            initialFilesGetter:
              initialFiles && initialFiles.length ? () => initialFiles : null,
          });
          _preWarmedProxies.set(preset, controller.proxy);
        },

        /**
         * Creates either a functional workspace, or a dummy model with the same method signatures
         *
         * @param {boolean} enabled - If true, returns a workspace. Otherwise returns a dummy object.
         * @param {Solution?} solution - If provided, will be used to determine language server
         * @param {Challenge?} challenge - If provided, will be used to determine language server
         * @param {Function?} initialFilesGetter - If provided, configures the unopened files automatically.
         */
        createIfEnabled({ enabled, solution, challenge, initialFilesGetter }) {
          let svc;
          if (enabled) {
            try {
              svc = this.create({ solution, challenge, initialFilesGetter });
            } catch (err) {
              // $log.error automatically sends to Sentry
              $log.error(
                '[SmartIDE] Failed to create workspace',
                {
                  error: err.message || String(err),
                  challengeId: challenge && challenge.id,
                  language:
                    challenge &&
                    challenge.runConfig &&
                    challenge.runConfig.language,
                  languageVersion:
                    challenge &&
                    challenge.runConfig &&
                    challenge.runConfig.languageVersion,
                  hasLanguageServers: !!(
                    challenge &&
                    challenge.runConfig &&
                    challenge.runConfig.languageServers &&
                    challenge.runConfig.languageServers.length
                  ),
                  solutionId: solution && solution.id,
                },
                err,
              );
            }
          }
          return (
            svc ||
            eventEmitter({
              enabled: false,
              status: WORKSPACE_STATUS.DISABLED,
              initializeFiles: $q.resolve,
              addFile: $q.resolve,
              updateFile: $q.resolve,
              deleteFile: $q.resolve,
              renameFile: $q.resolve,
              hideAllPopups: noop,
              enablePopups: noop,
              openEditor: noop,
              saveOpenFile: $q.resolve,
              saveAllFiles: $q.resolve,
              closeFile: $q.resolve,
              destroy: noop,
            })
          );
        },
        /**
         * Creates a workspace manager. Requires either challenge or solution & solution.challenge.
         *
         * @param {Solution?} solution - If provided, will be used to determine language server
         * @param {Challenge?} challenge - If provided, will be used to determine language server
         * @param {Function?} initialFilesGetter - If provided, configures the unopened files automatically.
         */
        // eslint-disable-next-line no-unused-vars
        create({
          solution,
          challenge = solution && solution.challenge,
          initialFilesGetter = noop,
        } = {}) {
          if (!$window.CodeMirrorWorkspace) {
            throw new Error('Unable to find CodeMirrorWorkspace');
          }
          if (!challenge) {
            throw new Error('Missing required challenge');
          }

          // Ensure our CodeMirror-level instrumentation is installed once.
          // This is safe even if SmartIDE is later disabled because it only records when
          // we attach per-editor context (see openEditor).
          installSmartIdeAutocompleteInstrumentation();

          const runConfig = challenge.runConfig;
          const supportedServers = new Set(runConfig.languageServers || []);
          var _disconnectedAt = null;
          var smartIdeSessionId = generateSmartIdeSessionId();
          if (supportedServers.size === 0) {
            // REVIEW This should probably be checked in `createIfEnabled` instead.
            throw new Error('No Language Server is available');
          }
          // Shared state for this workspace — passed by reference to every openEditor call
          // so all editors report startup metrics against the same workspace start time.
          var workspaceState = {
            startTime: Date.now(),
            lspStartupRecorded: false,
            firstTypingRecorded: false,
          };

          // REVIEW Creating `LanguageServerController` starts Proxy service based on `runConfig`.
          const controller = new LanguageServerController({
            url: configService.config.languageServerUrl,
            runConfig,
            $log,
            initialFilesGetter,
            context: {
              smartIdeSessionId: smartIdeSessionId,
              assessmentId:
                (solution && solution.assessmentId) ||
                (solution && solution.assessment && solution.assessment.id),
              challengeId: challenge && challenge.id,
              solutionId: solution && solution.id,
              candidateId:
                appConfig.candidateApp && solution && solution.candidateId,
              teamMemberId:
                !appConfig.candidateApp && solution && solution.teamMemberId,
              language: runConfig && runConfig.language,
              languageVersion: runConfig && runConfig.languageVersion,
              preset: runConfig && runConfig.preset,
            },
            authorize() {
              return $http
                .post(`${appConfig.baseApiRoot}service_auth`, {
                  service: 'language_server',
                  teamId: challenge.teamId,
                })
                .then((resp) => resp.data.token);
            },
          });

          const getLanguageInfo = (uri) => {
            const lang = languages.langFromFilename(uri);
            if (!lang || !(lang.languageServerIds || []).length) return null;

            return {
              languageId: lang.languageId,
              languageServerIds: lang.languageServerIds,
            };
          };

          // This error happens when we dispose (destroy) the workspace while a request is in-progress.
          // We can always ignore these errors.
          const isDisposeError = (err) =>
            /pending response rejected/i.test(err) ||
            /request cancelled/i.test(err);
          // Both handlers below run per file operation, so an unrecoverable
          // proxy reports through them on every keystroke. Dedupe per distinct
          // failure for the life of this workspace.
          const reportFailure = createFailureReporter($log);

          const catchAndIgnoreDisposeErrors = (err) => {
            if (!isDisposeError(err)) {
              // Reported once per distinct error - $log.error sends to Sentry
              reportFailure('[SmartIDE] Workspace operation failed:', err, {
                error: err.message || String(err),
                language: runConfig.language,
                languageVersion: runConfig.languageVersion,
                challengeId: challenge.id,
                solutionId: solution && solution.id,
              });
              return $q.reject(err);
            }
          };

          const catchLog = (err) => {
            if (!isDisposeError(err)) {
              // Reported once per distinct error - $log.error sends to Sentry
              reportFailure('[SmartIDE] Cleanup operation failed:', err, {
                error: err.message || String(err),
                language: runConfig.language,
                challengeId: challenge.id,
              });
            }
            return err;
          };

          // Wrap workspace creation to catch any errors from CodeMirrorWorkspace
          let workspace;
          try {
            // Set base context immediately so any early errors get tagged.
            sentrySetSmartIdeContext({
              smartIdeSessionId: smartIdeSessionId,
              challengeId: challenge && challenge.id,
              solutionId: solution && solution.id,
              language: runConfig && runConfig.language,
              languageVersion: runConfig && runConfig.languageVersion,
              preset: runConfig && runConfig.preset,
            });

            workspace = new $window.CodeMirrorWorkspace({
              // Using relative URI.
              // Proxy remaps to appropriate file path.
              rootUri: 'source://',
              getLanguageAssociation: (uri) => {
                const info = getLanguageInfo(uri);
                if (!info) return null;

                const languageServerIds = info.languageServerIds.filter((id) =>
                  supportedServers.has(id),
                );
                if (languageServerIds.length === 0) return null;
                return {
                  languageId: info.languageId,
                  languageServerIds,
                };
              },
              getConnectionString: async (id) => {
                if (!id) return '';
                var connStartTime = Date.now();
                sentryBreadcrumb('LSP getConnectionString', {
                  serverId: id,
                  language: runConfig.language,
                });

                // Keep server id on the Sentry scope for any connection-related errors.
                sentrySetSmartIdeContext({
                  smartIdeSessionId: smartIdeSessionId,
                  serverId: id,
                  challengeId: challenge && challenge.id,
                  solutionId: solution && solution.id,
                  language: runConfig && runConfig.language,
                  languageVersion: runConfig && runConfig.languageVersion,
                  preset: runConfig && runConfig.preset,
                });
                var url = await controller.start(id);
                sentryMeasure(
                  'lsp.getConnectionString',
                  Date.now() - connStartTime,
                  { serverId: id, language: runConfig.language },
                );
                return url;
              },
              onDisconnected: (serverId) => {
                _disconnectedAt = Date.now();
                var tabHidden = document.visibilityState === 'hidden';
                sentryBreadcrumb(
                  'LSP server disconnected',
                  {
                    serverId: serverId,
                    language: runConfig.language,
                    languageVersion: runConfig.languageVersion,
                    challengeId: challenge.id,
                    tabHidden: tabHidden,
                    cause: tabHidden ? 'browser_tab_background' : 'unknown',
                  },
                  'warning',
                );
                $log.warn(
                  '[SmartIDE] LSP server disconnected, attempting to reconnect...',
                  {
                    serverId,
                    language: runConfig.language,
                    languageVersion: runConfig.languageVersion,
                    challengeId: challenge.id,
                    solutionId: solution && solution.id,
                    tabHidden: tabHidden,
                    cause: tabHidden ? 'browser_tab_background' : 'unknown',
                  },
                );
                setStatus(WORKSPACE_STATUS.CONNECTION_RETRYING);
              },
              onReconnected: (serverId) => {
                var downtime = _disconnectedAt
                  ? Date.now() - _disconnectedAt
                  : 0;
                sentryMeasure('lsp.reconnection.downtime', downtime, {
                  serverId: serverId,
                  language: runConfig.language,
                  challengeId: challenge.id,
                });
                sentryBreadcrumb('LSP server reconnected', {
                  serverId: serverId,
                  language: runConfig.language,
                  downtimeMs: downtime,
                });
                _disconnectedAt = null;
                $log.info('[SmartIDE] LSP server reconnected successfully', {
                  serverId,
                  language: runConfig.language,
                });
                setStatus();
                // Reconnected to the server and finished re-initialization.
                // Sync the files if necessary.
                // It's possible that the files are out of sync now if files were changed while the connection was down.
                // This function should return `Promise<void>` that resolves after `workspace.notifyFilesChanged(changes)`.
                // The event handlers related to the connection is reattached to the editors after this.
                return Promise.resolve();
              },
              onConnectionFailed: (serverId) => {
                var downtime = _disconnectedAt
                  ? Date.now() - _disconnectedAt
                  : 0;
                var tabHidden = document.visibilityState === 'hidden';
                var cause = tabHidden
                  ? 'browser_tab_background'
                  : 'server_or_network_error';
                sentryBreadcrumb(
                  'LSP connection failed permanently',
                  {
                    serverId: serverId,
                    language: runConfig.language,
                    languageVersion: runConfig.languageVersion,
                    preset: runConfig.preset,
                    challengeId: challenge.id,
                    downtimeMs: downtime,
                    tabHidden: tabHidden,
                    cause: cause,
                  },
                  tabHidden ? 'warning' : 'error',
                );
                _disconnectedAt = null;
                if (tabHidden) {
                  // Tab was in background - browser throttled WebSocket pings.
                  // Not a real error, log as warning to avoid Sentry noise.
                  $log.warn(
                    '[SmartIDE] LSP connection lost (tab was in background)',
                    {
                      serverId,
                      language: runConfig.language,
                      languageVersion: runConfig.languageVersion,
                      preset: runConfig.preset,
                      challengeId: challenge.id,
                      solutionId: solution && solution.id,
                      downtimeMs: downtime,
                      cause: cause,
                    },
                  );
                } else {
                  // Real failure - $log.error automatically sends to Sentry
                  $log.error(
                    '[SmartIDE] LSP server connection failed permanently',
                    {
                      serverId,
                      language: runConfig.language,
                      languageVersion: runConfig.languageVersion,
                      preset: runConfig.preset,
                      challengeId: challenge.id,
                      solutionId: solution && solution.id,
                      downtimeMs: downtime,
                      cause: cause,
                    },
                  );
                }
                // Failed all the reconnection attempts — the proxy is gone.
                // Respawn it once so the candidate isn't left with a dead IDE
                // for the rest of a timed assessment.
                respawnProxy(cause);
              },
              renderMarkdown: marked,
              configs: {
                'pyright-langserver': {
                  settings: {
                    python: {
                      analysis: {
                        autoSearchPaths: true,
                        useLibraryCodeForTypes: true,
                        diagnosticMode: 'openFiles',
                      },
                    },
                  },
                },
                'typescript-language-server': {
                  settings: {
                    diagnostics: {
                      ignoredCodes: [
                        // Ignore confusing "File is a CommonJS module; it may be converted to an ES6 module."
                        80001,
                      ],
                    },
                  },
                },
                'vue-language-server': {
                  initOptions: {
                    typescript: {
                      // Need to pass absolute path to `tsserverlibrary` :(
                      serverPath:
                        '/workspace/vue/node_modules/typescript/lib/tsserverlibrary.js',
                      localizedPath: null,
                    },
                    languageFeatures: {
                      hover: true,
                      signatureHelp: true,
                      diagnostics: true,
                      references: true,
                      implementation: true,
                      definition: true,
                      typeDefinition: true,
                      documentHighlight: true,
                      documentLink: true,
                      completion: {
                        defaultTagNameCase: 'both',
                        defaultAttrNameCase: 'kebabCase',
                        getDocumentNameCasesRequest: false,
                        getDocumentSelectionRequest: false,
                      },
                    },
                  },
                },
              },
            });
          } catch (err) {
            $log.error(
              '[SmartIDE] Failed to initialize CodeMirrorWorkspace',
              {
                error: err.message || String(err),
                language: runConfig.language,
                languageVersion: runConfig.languageVersion,
                challengeId: challenge.id,
              },
              err,
            );
            throw err;
          }

          // Monkey-patch workspace methods to capture LSP errors that might be swallowed
          const originalOpenTextDocument = workspace.openTextDocument;
          workspace.openTextDocument = function (path, codemirror) {
            return originalOpenTextDocument
              .call(workspace, path, codemirror)
              .catch((err) => {
                // This catch will capture errors from the LSP that openTextDocument might swallow
                if (!isDisposeError(err)) {
                  $log.error(
                    '[SmartIDE] LSP error in openTextDocument',
                    {
                      path,
                      error: err.message || String(err),
                      language: runConfig.language,
                    },
                    err,
                  );
                }
                throw err;
              });
          };

          /**
           * Returns a simplified file object with a mapped path (mapped for reference files).
           * The method returns false in the following circumstances
           * - the file is not present
           * - it is a directory
           * - it is missing the path
           * - it is hidden, and we have a solution
           * - it is a reference file, and we have a solution
           * @param file
           * @returns {boolean|{path: string, contents: string}}
           * @private
           */
          const _mappedFile = (file) => {
            if (
              !file ||
              file.directory ||
              !file.path ||
              (solution && file.hidden) ||
              (solution && file.reference)
            ) {
              return false;
            }
            let path = file.path;
            if (file.reference) {
              path = '$ref/' + path;
            }
            return { contents: file.contents, path };
          };

          const connectingEditors = {};
          // path -> {file, codemirror}, so a respawned proxy can reattach the
          // editors the candidate actually has open.
          const openEditors = {};
          const openEditorList = () =>
            Object.keys(openEditors).map((p) => openEditors[p]);

          const svc = {
            enabled: true,
            status: WORKSPACE_STATUS.NEW,

            initializeFiles: tryFunction(function (files) {
              const mapped = files.map(_mappedFile).filter(identity);
              return $q
                .resolve(controller.writeFiles(mapped))
                .catch(catchAndIgnoreDisposeErrors)
                .then(
                  (changes) => changes && workspace.notifyFilesChanged(changes),
                );
            }),
            addFile: tryFunction(function (file) {
              const mappedFile = _mappedFile(file);
              if (!mappedFile) return $q.resolve();

              return $q
                .resolve(controller.writeFile(mappedFile))
                .catch(catchAndIgnoreDisposeErrors)
                .then(
                  (changes) => changes && workspace.notifyFilesChanged(changes),
                );
            }),
            updateFile: tryFunction(function (file) {
              const mappedFile = _mappedFile(file);
              if (!mappedFile) return $q.resolve();

              return $q
                .resolve(controller.writeFile(mappedFile))
                .catch(catchAndIgnoreDisposeErrors)
                .then(
                  (changes) => changes && workspace.notifyFilesChanged(changes),
                );
            }),
            deleteFile: tryFunction(function (file) {
              const mappedFile = _mappedFile(file);
              if (!mappedFile) return $q.resolve();

              return $q
                .resolve(controller.removeFile(mappedFile.path))
                .catch(catchAndIgnoreDisposeErrors)
                .then(
                  (changes) => changes && workspace.notifyFilesChanged(changes),
                );
            }),
            renameFile: tryFunction(function (file, oldPath) {
              const mappedFile = _mappedFile(file);
              if (!mappedFile) return $q.resolve();

              return $q
                .resolve(controller.renameFile(oldPath, mappedFile.path))
                .catch(catchAndIgnoreDisposeErrors)
                .then(
                  (changes) => changes && workspace.notifyFilesChanged(changes),
                );
            }),
            hideAllPopups: tryFunction(function () {
              workspace.hideAllPopups();
            }),
            enablePopups: tryFunction(function (popupsEnabled) {
              workspace.enablePopups(popupsEnabled);
            }),

            /**
             * Opens a file in an editor within the workspace
             */
            openEditor: tryFunction(function (file, codemirror) {
              const mappedFile = _mappedFile(file);
              if (!mappedFile) return;
              const path = mappedFile.path;

              openEditors[path] = { file, codemirror };
              connectingEditors[path] = Date.now();
              var openStartTime = Date.now();
              sentryBreadcrumb('LSP openEditor start', {
                path: path,
                language: runConfig.language,
              });

              setStatus();

              return $q
                .resolve(workspace.openTextDocument(path, codemirror))
                .then(() => {
                  var openDuration = Date.now() - openStartTime;
                  sentryMeasure('lsp.openEditor', openDuration, {
                    path: path,
                    language: runConfig.language,
                    challengeId: challenge.id,
                  });
                  sentryBreadcrumb('LSP openEditor complete', {
                    path: path,
                    durationMs: openDuration,
                  });

                  if (
                    !workspaceState.lspStartupRecorded &&
                    workspaceState.startTime
                  ) {
                    workspaceState.lspStartupRecorded = true;
                    controller.emitStartupResult('ok', {
                      duration_ms: Date.now() - workspaceState.startTime,
                      reason: 'open_text_document_ready',
                    });
                    trackPerformance('smartide.lsp.startup', {
                      duration_ms: Date.now() - workspaceState.startTime,
                      preset: runConfig.preset,
                      language: runConfig.language,
                      session_id: smartIdeSessionId,
                      status: 'ok',
                      challenge_id: challenge.id,
                      solution_id: solution && solution.id,
                      assessment_id:
                        (solution && solution.assessmentId) ||
                        (solution &&
                          solution.assessment &&
                          solution.assessment.id),
                    });
                  }

                  // Attach typing tracker now that the editor is opened within the workspace.
                  // This enables the global showHint wrapper to record time-to-first-autocomplete.
                  attachSmartIdeFirstTypingTracker(
                    codemirror,
                    {
                      filePath: path,
                      language: runConfig.language,
                      languageVersion: runConfig.languageVersion,
                      preset: runConfig.preset,
                      challengeId: challenge.id,
                      solutionId: solution && solution.id,
                      assessmentId:
                        (solution && solution.assessmentId) ||
                        (solution &&
                          solution.assessment &&
                          solution.assessment.id),
                      workspaceState: workspaceState,
                    },
                    $interval,
                    $timeout,
                  );

                  delete connectingEditors[path];
                  setStatus();
                })
                .catch((err) => {
                  delete connectingEditors[path]; // should we keep it here?
                  if (isDisposeError(err)) return;

                  // $log.error automatically sends to Sentry with context
                  $log.error(
                    '[SmartIDE] Failed to open editor',
                    {
                      filePath: path,
                      language: runConfig.language,
                      languageVersion: runConfig.languageVersion,
                      errorMessage: err.message || String(err),
                      challengeId: challenge.id,
                      solutionId: solution && solution.id,
                    },
                    err,
                  );

                  controller.emitStartupResult('connection_failed', {
                    reason: 'open_text_document_failed',
                  });

                  // A failed proxy takes every open editor down with it. Don't
                  // let those knock-on failures downgrade the terminal status
                  // the candidate was just alerted about.
                  if (svc.status !== WORKSPACE_STATUS.CONNECTION_FAILED) {
                    setStatus(WORKSPACE_STATUS.CONNECTION_ERROR);
                  }
                  return $q.reject(err);
                });
            }),

            /**
             * Saves changes to an open file back to the workspace,
             * which can be used even while the file isn't open
             */
            saveOpenFile: tryFunction(function (file) {
              const mappedFile = _mappedFile(file);
              if (!mappedFile) return $q.resolve();

              return $q
                .resolve(workspace.saveTextDocument(mappedFile.path))
                .catch(catchAndIgnoreDisposeErrors);
            }),

            /**
             * saves all open files
             */
            saveAllFiles: function () {
              // `saveOpenFile` expects a file, not a path -- passing a path
              // makes `_mappedFile` return false and the save a no-op.
              return $q.all(
                openEditorList().map((e) => svc.saveOpenFile(e.file)),
              );
            },

            /**
             * Closes an open file (*does not save the file*
             */
            closeFile: tryFunction(function (file) {
              const mappedFile = _mappedFile(file);
              if (!mappedFile) return $q.resolve();
              const path = mappedFile.path;

              delete openEditors[path];

              return $q
                .resolve(workspace.closeTextDocument(path))
                .catch(catchLog)
                .then(() => {
                  delete connectingEditors[path];
                  setStatus();
                });
            }),
            destroy: tryFunction(function () {
              // destroy all listeners
              svc.off(true);
              setStatus(WORKSPACE_STATUS.DESTROYING);
              const destroyed = controller.destroyProxy().catch(catchLog);
              // The closes now actually run (they used to be handed a path,
              // which `_mappedFile` rejected), so they have to finish before
              // dispose or every `closeTextDocument` races the teardown.
              return $q
                .all(openEditorList().map((e) => svc.closeFile(e.file)))
                .then(() => workspace.dispose())
                .catch(catchLog)
                .then(() => destroyed)
                .then(() => {
                  setStatus(WORKSPACE_STATUS.DISABLED);
                  sentryClearSmartIdeContext();
                });
            }),
          };

          eventEmitter(svc);

          function setStatus(status) {
            if (!status) {
              // if there are no editors, we're connected
              status = WORKSPACE_STATUS.CONNECTED;

              const now = Date.now();
              Object.keys(connectingEditors).forEach((k) => {
                const delay = now - connectingEditors[k];
                if (delay > CONNECTING_WAIT_ERROR) {
                  // if it has taken too long, show an error
                  status = WORKSPACE_STATUS.CONNECTION_ERROR;
                } else if (status === WORKSPACE_STATUS.CONNECTED) {
                  if (delay > CONNECTING_WAIT_DELAY) {
                    // if it is taking a while for any of them, show a warning color
                    status = WORKSPACE_STATUS.CONNECTION_DELAY;
                  } else {
                    // otherwise just show that we're connecting
                    status = WORKSPACE_STATUS.CONNECTING;
                  }
                }
              });
            }
            const oldStatus = svc.status;
            svc.status = status;
            if (oldStatus !== status) {
              svc.emit(status);
            }
          }

          /**
           * Replaces a dead proxy, re-seeds its workspace files, and reattaches
           * the open editors to it. `controller.respawn()` is one-shot, so a
           * second failure lands the workspace in CONNECTION_FAILED for good.
           *
           * @param {string} cause - Why the connection failed, for telemetry
           * @returns {Promise} Resolves once the workspace has settled
           */
          function respawnProxy(cause) {
            const failed = () => {
              controller.emitStartupResult('connection_failed', {
                reason: cause,
              });
              setStatus(WORKSPACE_STATUS.CONNECTION_FAILED);
            };

            const editors = openEditorList();

            setStatus(WORKSPACE_STATUS.CONNECTION_RETRYING);
            return $q
              .resolve(controller.respawn())
              .then((proxy) => {
                if (!proxy) return failed();
                return reattachEditors({
                  svc,
                  editors,
                  files: initialFilesGetter && initialFilesGetter(),
                  all: (promises) => $q.all(promises),
                }).then(() => setStatus());
              })
              .catch(failed);
          }

          controller.proxy
            .then(() => {
              if (initialFilesGetter) {
                svc.initializeFiles(initialFilesGetter());
              }
            })
            .catch(() => {
              // A pod that never came up is a dead proxy like any other, so it
              // recovers through the same one path: `respawnProxy` re-seeds the
              // replacement's files, reattaches the editors, moves the status
              // and reports the outcome. Rebuilding from `start()` instead
              // would hand the candidate a pod with none of the challenge
              // files on it, which reads as a working IDE giving wrong answers.
              // One shot, then CONNECTION_FAILED and its actionable alert.
              respawnProxy('proxy_creation_failed');
            });

          return svc;
        },
      };
    },
  );
